import { Fragment, useId, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  apiLoadErrorText,
  useGetPrcDiffQuery,
  type PrcDiffArticle,
  type PrcDiffItem,
  type PrcDiffStatus,
  type PrcVersionPayload,
  type PrcVersionSummary,
} from '@shared/api';
import { Select } from '@shared/ui/Input';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconChevronRight } from '@shared/ui/icons';
import {
  FleetChip,
  NBSP,
  ScrollRegion,
  VisuallyHidden,
  fleetLeading,
  fleetType,
  formatCount,
  typo,
} from '@entities/fleet';
import {
  articleLabel,
  compareDecimal,
  decimalSign,
  diffStatusLabel,
  diffStatusTone,
  isTotalArticle,
  prcStatusLabel,
  shiftDecimal,
  toDecimalText,
} from '@entities/prc';
import { FieldLabel, Notice, TextButton } from '@widgets/FleetRegistry/ui';
import { prcChartColors } from '../chartTokens';
import { MODELS, count, formatMoneyCell, formatPercent, formatRub, formatShare } from '../format';
import { NARROW, NumTable, SegmentButton, Segmented, StatGrid, Toolbar, YearSwitch } from '../ui';

type Sort = 'category' | 'change';
type Show = 'all' | PrcDiffStatus;

const AgainstBox = styled.div`
  flex: 0 1 360px;
  min-width: 0;

  & > label {
    margin-bottom: ${({ theme }) => theme.spacing(1)};
  }

  @container prc (max-width: ${NARROW}) {
    flex-basis: 100%;
  }
`;

const Tiles = styled(StatGrid)`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const Controls = styled(Toolbar)`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const TableBox = styled.div<{ $stale: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(3)};
  opacity: ${({ $stale }) => ($stale ? 0.55 : 1)};
  transition: opacity ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
`;

const Table = styled(NumTable)`
  td.c-name {
    min-width: 260px;
  }

  td.c-toggle {
    width: 36px;
    padding-right: 0;
  }

  tr.details > td {
    padding: ${({ theme }) => `0 ${theme.spacing(2.5)} ${theme.spacing(4)}`};
    background: ${({ theme }) => theme.colors.background};
  }

  .narrow-only {
    display: none;
  }

  /* Карточки — только строки самой таблицы моделей (дочерние селекторы):
     вложенная таблица статей остаётся таблицей со своей прокруткой. */
  @container prc (max-width: ${NARROW}) {
    &,
    & > tbody,
    & > tbody > tr,
    & > tbody > tr > th,
    & > tbody > tr > td {
      display: block;
    }

    & > thead,
    .wide-only {
      display: none;
    }

    .narrow-only {
      display: block;
    }

    tr.row {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      column-gap: ${({ theme }) => theme.spacing(2)};
      padding: ${({ theme }) => `${theme.spacing(3)} 0`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    tr.row > td {
      padding: 0;
      border: none;
      background: none;
      min-width: 0;
    }

    tr.details > td {
      padding: ${({ theme }) => `0 0 ${theme.spacing(4)}`};
    }

    tr.group > th {
      padding: ${({ theme }) => `${theme.spacing(5)} 0 ${theme.spacing(2)}`};
    }
  }
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & > svg {
    transition: transform ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  }

  &[aria-expanded='true'] > svg {
    transform: rotate(90deg);
  }
`;

const Name = styled.span`
  display: block;
  font-size: ${fleetType.base};
  font-weight: 500;
  line-height: ${fleetLeading.tight};
  overflow-wrap: anywhere;
`;

const Sub = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const NarrowNums = styled.span`
  display: block;
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  font-size: ${fleetType.body};
  font-variant-numeric: tabular-nums;

  & > strong {
    font-weight: 600;
  }
`;

const ArticleTable = styled(NumTable)`
  margin-top: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;

  td,
  tbody th {
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }

  td.c-share {
    min-width: 200px;
  }

  /* На узком листе доля — числом: полоса удлинила бы прокрутку таблицы. */
  @container prc (max-width: ${NARROW}) {
    td.c-share {
      min-width: 0;
    }

    td.c-share [data-share-track] {
      display: none;
    }
  }
`;

const ShareCell = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
`;

/** Полоса доли статьи в изменении себестоимости: длина — доля, цвет — подорожала или подешевела. */
const ShareTrack = styled.span`
  position: relative;
  flex: none;
  width: 96px;
  height: 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surface};
  overflow: hidden;
`;

const ShareFill = styled.span<{ $dir: 'up' | 'down'; $pct: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${({ $pct }) => `${$pct}%`};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme, $dir }) => prcChartColors(theme)[$dir === 'up' ? 'up' : 'down']};
`;

const Legend = styled.p`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Key = styled.span<{ $dir: 'up' | 'down' }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};

  &::before {
    content: '';
    width: 16px;
    height: 8px;
    border-radius: ${({ theme }) => theme.radius.pill};
    background: ${({ theme, $dir }) => prcChartColors(theme)[$dir]};
  }
`;

/** Ширина полосы доли, %: |доля| × 100, не больше 100 (доля бывает больше единицы). */
function sharePercent(share: string | null): number {
  const d = toDecimalText(share);
  if (d === null) return 0;
  const pct = shiftDecimal(d.replace(/^-/, ''), 2);
  if (pct === null || decimalSign(pct) === 0) return 0;
  if ((compareDecimal(pct, '100') ?? 0) >= 0) return 100;
  // Число здесь — только ширина полосы на экране (целые проценты); подпись рядом —
  // точная строка сервера.
  return Math.max(2, Math.round(Number(pct)));
}

function ArticlesTable({ item, idPrefix }: { item: PrcDiffItem; idPrefix: string }) {
  return (
    <ScrollRegion label={`Статьи: ${item.model_name}`}>
      <ArticleTable id={idPrefix}>
        <thead>
          <tr>
            <th scope="col">Статья</th>
            <th scope="col" className="num">
              Было
            </th>
            <th scope="col" className="num">
              Стало
            </th>
            <th scope="col" className="num">
              Изменение, ₽
            </th>
            <th scope="col" className="num">
              %
            </th>
            <th scope="col" className="num">
              Доля в изменении себестоимости
            </th>
          </tr>
        </thead>
        <tbody>
          {item.articles.map((a: PrcDiffArticle) => {
            const total = isTotalArticle(a.article);
            const dir = decimalSign(a.delta ?? '0') === -1 ? 'down' : 'up';
            const width = sharePercent(a.share);
            return (
              <tr key={a.article} className={a.article === 'cost' ? 'total' : undefined}>
                <th scope="row" className={total ? 'strong' : undefined}>
                  {articleLabel(a.article)}
                </th>
                <td className="num">{formatMoneyCell(a.before)}</td>
                <td className={`num${total ? ' strong' : ''}`}>{formatMoneyCell(a.after)}</td>
                <td className="num">{formatMoneyCell(a.delta, true)}</td>
                <td className="num">{a.pct === null ? '—' : formatPercent(a.pct, 2, true)}</td>
                <td className="num c-share">
                  {a.share === null || decimalSign(a.share) === 0 ? (
                    <span aria-hidden="true">—</span>
                  ) : (
                    <ShareCell>
                      <ShareTrack aria-hidden="true" data-share-track="">
                        {width > 0 && <ShareFill $dir={dir} $pct={width} />}
                      </ShareTrack>
                      {formatShare(a.share, 1, true)}
                    </ShareCell>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </ArticleTable>
    </ScrollRegion>
  );
}

function compareChange(a: PrcDiffItem, b: PrcDiffItem): number {
  const key = (x: PrcDiffItem) => (x.pct ?? '0').replace(/^-/, '');
  return compareDecimal(key(b), key(a)) ?? 0;
}

/**
 * Вкладка «Было → стало»: против действующих ПРЦ справочника (по умолчанию)
 * или другой версии. Сводка, цены моделей было/стало, раскрытие — статьи с
 * долей каждой в изменении себестоимости: из-за чего выросла цена.
 */
export function DiffTab({
  payload,
  versions,
  year,
  onYear,
  against,
  onAgainst,
}: {
  payload: PrcVersionPayload;
  versions: readonly PrcVersionSummary[];
  year: number;
  onYear: (year: number) => void;
  against: string;
  onAgainst: (value: string) => void;
}) {
  const uid = useId();
  const v = payload.version;
  const variant = payload.variant ?? null;
  const { data, error, isLoading, isFetching, refetch } = useGetPrcDiffQuery({
    id: v.id,
    against: against === 'current' ? undefined : against,
    year,
    variant: variant?.id,
  });
  const [show, setShow] = useState<Show>('all');
  const [sort, setSort] = useState<Sort>('category');
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const others = versions.filter((x) => x.id !== v.id);

  const items = useMemo(() => {
    const list = (data?.items ?? []).filter((i) => show === 'all' || i.status === show);
    return sort === 'change' ? [...list].sort(compareChange) : list;
  }, [data, show, sort]);

  const groups = useMemo(() => {
    if (sort === 'change') return [{ key: 'all', title: null, items }];
    const out: { key: string; title: string | null; items: PrcDiffItem[] }[] = [];
    for (const i of items) {
      const title = i.category_name ?? 'Без категории';
      const last = out[out.length - 1];
      if (last && last.title === title) last.items.push(i);
      else out.push({ key: `${title}-${out.length}`, title, items: [i] });
    }
    return out;
  }, [items, sort]);

  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const s = data?.summary;
  const noCurrent = against === 'current' && s !== undefined && s.added === s.models;
  const stats: { id: Show; label: string; n: number }[] = s
    ? [
        { id: 'increased', label: 'Выросла', n: s.increased },
        { id: 'decreased', label: 'Снизилась', n: s.decreased },
        { id: 'unchanged', label: 'Без изменений', n: s.unchanged },
        { id: 'added', label: 'Новые', n: s.added },
      ]
    : [];
  const errors = (data?.items ?? []).filter((i) => i.status === 'error').length;

  return (
    <>
      <Toolbar>
        <AgainstBox>
          <FieldLabel htmlFor={`${uid}-against`}>Сравнить с</FieldLabel>
          <Select id={`${uid}-against`} value={against} onChange={(e) => onAgainst(e.target.value)}>
            <option value="current">
              {variant
                ? 'Действующими ПРЦ на объектах варианта'
                : 'Действующими ПРЦ справочника техники'}
            </option>
            {variant && <option value="base">Основным расчётом этой версии</option>}
            {others.length > 0 && (
              <optgroup label="Другой версией">
                {others.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label} — {prcStatusLabel(o.status).toLowerCase()}
                  </option>
                ))}
              </optgroup>
            )}
          </Select>
        </AgainstBox>
        <YearSwitch
          years={v.params.years}
          baseYear={v.params.base_year}
          value={year}
          onChange={onYear}
        />
      </Toolbar>

      {error !== undefined && !data ? (
        <Controls>
          <Notice $tone="danger" role="alert">
            <span className="text">
              {apiLoadErrorText(error, 'Не удалось сравнить версии.', 'Нет доступа к ПРЦ.')}
            </span>
            <TextButton type="button" onClick={() => void refetch()}>
              Повторить
            </TextButton>
          </Notice>
        </Controls>
      ) : isLoading || !data || !s ? (
        <Controls aria-busy="true">
          <SkeletonLine $height={64} />
          <SkeletonLine $width="70%" />
          <SkeletonLine $width="90%" />
        </Controls>
      ) : (
        <>
          <Tiles $cols={4} aria-label="Сводка изменений цены без НДС">
            {stats.map((t) => (
              <div key={t.id}>
                <dt>{t.label}</dt>
                <dd className="big">{formatCount(t.n)}</dd>
              </div>
            ))}
          </Tiles>
          {noCurrent && (
            <Controls>
              <Notice $tone="info">
                <span className="text">
                  {typo(
                    `Действующих ПРЦ за ${data.year} год в справочнике нет — все модели новые. Сравните с другой версией, чтобы увидеть «было → стало».`,
                  )}
                </span>
              </Notice>
            </Controls>
          )}
          {data.removed.length > 0 && (
            <Controls>
              <Notice $tone="warning">
                <span className="text">
                  {typo(
                    `Убраны из версии, хотя цена была (${data.removed.length}): ${data.removed
                      .map(
                        (r) =>
                          r.model_name ||
                          `модель без названия в действующих ценах (${r.model_id.slice(0, 8)})`,
                      )
                      .join('; ')}.`,
                  )}
                </span>
              </Notice>
            </Controls>
          )}

          <Controls>
            <Segmented role="group" aria-label="Какие модели показать">
              <SegmentButton
                type="button"
                aria-pressed={show === 'all'}
                onClick={() => setShow('all')}
              >
                Все · {formatCount(s.models)}
              </SegmentButton>
              {[...stats, { id: 'error' as const, label: 'Ошибка', n: errors }]
                .filter((t) => t.n > 0)
                .map((t) => (
                  <SegmentButton
                    key={t.id}
                    type="button"
                    aria-pressed={show === t.id}
                    onClick={() => setShow(show === t.id ? 'all' : t.id)}
                  >
                    {t.label} · {formatCount(t.n)}
                  </SegmentButton>
                ))}
            </Segmented>
            <Segmented role="group" aria-label="Порядок">
              <SegmentButton
                type="button"
                aria-pressed={sort === 'category'}
                onClick={() => setSort('category')}
              >
                По категориям
              </SegmentButton>
              <SegmentButton
                type="button"
                aria-pressed={sort === 'change'}
                onClick={() => setSort('change')}
              >
                По изменению
              </SegmentButton>
            </Segmented>
          </Controls>

          <Legend>
            <span>
              {typo(
                `Цена без НДС, ₽${NBSP}на${NBSP}маш.-час, ${data.year} год. Раскройте модель — статьи и их доля в изменении себестоимости:`,
              )}
            </span>
            <Key $dir="up">подорожала</Key>
            <Key $dir="down">подешевела</Key>
          </Legend>

          <TableBox $stale={isFetching} aria-busy={isFetching}>
            {items.length === 0 ? (
              <Notice $tone="info">
                <span className="text">Таких моделей нет.</span>
              </Notice>
            ) : (
              <ScrollRegion label="Было → стало по моделям">
                <Table>
                  <thead>
                    <tr>
                      <th scope="col" className="c-toggle">
                        <VisuallyHidden>Статьи</VisuallyHidden>
                      </th>
                      <th scope="col">Модель</th>
                      <th scope="col" className="num">
                        Было
                      </th>
                      <th scope="col" className="num">
                        Стало
                      </th>
                      <th scope="col" className="num">
                        Изменение, ₽
                      </th>
                      <th scope="col" className="num">
                        %
                      </th>
                    </tr>
                  </thead>
                  {groups.map((g) => (
                    <tbody key={g.key}>
                      {g.title !== null && (
                        <tr className="group">
                          <th colSpan={6} scope="rowgroup">
                            {g.title}
                            <span className="meta">{count(g.items.length, MODELS)}</span>
                          </th>
                        </tr>
                      )}
                      {g.items.map((i) => {
                        const expanded = open.has(i.model_id);
                        const detailsId = `${uid}-d-${i.model_id}`;
                        return (
                          <Fragment key={i.model_id}>
                            <tr className="row">
                              <td className="c-toggle">
                                <Toggle
                                  type="button"
                                  aria-expanded={expanded}
                                  aria-controls={expanded ? detailsId : undefined}
                                  aria-label={`Статьи: ${i.model_name}`}
                                  onClick={() => toggle(i.model_id)}
                                >
                                  <IconChevronRight size={16} />
                                </Toggle>
                              </td>
                              <td className="c-name">
                                <Name>{i.model_name}</Name>
                                {(i.status === 'added' || i.status === 'error' || i.error) && (
                                  <Sub>
                                    <FleetChip $tone={diffStatusTone(i.status)}>
                                      {diffStatusLabel(i.status)}
                                    </FleetChip>
                                    {i.error && <span>{typo(i.error)}</span>}
                                  </Sub>
                                )}
                                <VisuallyHidden>{diffStatusLabel(i.status)}</VisuallyHidden>
                                <NarrowNums className="narrow-only">
                                  {formatRub(i.price_before)} →{' '}
                                  <strong>{formatRub(i.price_after)}</strong>
                                  {i.delta !== null &&
                                    ` · ${formatRub(i.delta, true)}${i.pct !== null ? `, ${formatPercent(i.pct, 2, true)}` : ''}`}
                                </NarrowNums>
                              </td>
                              <td className="wide-only num">{formatMoneyCell(i.price_before)}</td>
                              <td className="wide-only num strong">
                                {formatMoneyCell(i.price_after)}
                              </td>
                              <td className="wide-only num">{formatMoneyCell(i.delta, true)}</td>
                              <td className="wide-only num">
                                {i.pct === null ? '—' : formatPercent(i.pct, 2, true)}
                              </td>
                            </tr>
                            {expanded && (
                              <tr className="details">
                                <td colSpan={6}>
                                  <ArticlesTable item={i} idPrefix={detailsId} />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  ))}
                </Table>
              </ScrollRegion>
            )}
          </TableBox>
        </>
      )}
    </>
  );
}
